import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";

const EMPTY = {
  referralName: "",
  category: "",
  code: "",
  status: "Active",
};

const ReferralFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            referralName: record.referralName || "",
            category: record.category || "",
            code: record.code || "",
            status: record.status || "Active",
          }
        : EMPTY,
    );
  }, [isOpen, record]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    if (!form.referralName.trim()) {
      setErrors({ referralName: "Referral name is required" });
      return;
    }
    setSaving(true);
    try {
      await onSave?.({
        referralName: form.referralName.trim(),
        category: form.category.trim(),
        code: form.code.trim(),
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Referral Source" : "Create New Referral Source"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}
      <LibraryField
        id="ref-name"
        label="Referral Name"
        required
        error={errors.referralName}
      >
        <input
          id="ref-name"
          className={libraryInputClass(errors.referralName)}
          value={form.referralName}
          onChange={(e) => setField("referralName", e.target.value)}
          maxLength={255}
          autoFocus
        />
      </LibraryField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="ref-cat" label="Category">
          <input
            id="ref-cat"
            className={libraryInputClass()}
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            maxLength={100}
            placeholder="e.g. Hospital Discharge"
          />
        </LibraryField>
        <LibraryField id="ref-code" label="Code">
          <input
            id="ref-code"
            className={libraryInputClass()}
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            maxLength={50}
          />
        </LibraryField>
      </div>
      <LibraryField id="ref-status" label="Status">
        <div className="relative">
          <select
            id="ref-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default ReferralFormModal;
